import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Table,
  Drawer,
  Spin,
  Tag,
  Tooltip,
  Input,
  Select,
  message,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import {
  UserOutlined,
  MailOutlined,
  PhoneOutlined,
  CalendarOutlined,
  HomeOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";

import {
  AptGuest,
  AptGuestReservation,
  AptReservationStatus,
  GetAptGuestsParams,
  getAptGuests,
} from "../../../utils/api/apartmentRentalsAPI";

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmtDate = (d: string) => dayjs(d).format("DD.MM.YYYY");

const fmtCurrency = (n: number, currency?: string) => {
  if (currency === "EUR") return `€${new Intl.NumberFormat("de-DE").format(Math.round(n))}`;
  if (currency === "BAM") return `${new Intl.NumberFormat("bs-BA").format(Math.round(n))} KM`;
  return `${new Intl.NumberFormat("sr-RS").format(Math.round(n))} RSD`;
};

// Format a per-currency map into a readable string
const fmtByCurrency = (byCurrency: Record<string, number>) => {
  const entries = Object.entries(byCurrency).filter(([, v]) => v > 0);
  if (entries.length === 0) return "—";
  return entries.map(([cur, amt]) => fmtCurrency(amt, cur)).join(" + ");
};

const initials = (name: string) =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0])
    .join("")
    .toUpperCase();

const AVATAR_COLORS = [
  "#5b50d6", "#0e7490", "#ca8a04", "#16a34a",
  "#dc2626", "#7c3aed", "#0284c7", "#d97706",
];
const avatarColor = (key: string) => {
  let hash = 0;
  for (let i = 0; i < key.length; i++) hash = key.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

const STATUS_LABEL: Record<AptReservationStatus, string> = {
  confirmed: "Potvrđena",
  pending: "Pending",
  staying: "U boravku",
  checkout: "Check-out danas",
  canceled: "Otkazana",
  noshow: "No-show",
};

const STATUS_COLOR: Record<AptReservationStatus, string> = {
  confirmed: "blue",
  pending: "gold",
  staying: "green",
  checkout: "cyan",
  canceled: "red",
  noshow: "default",
};

const CHANNEL_LABEL: Record<string, string> = {
  direct: "Direktno",
  booking: "Booking",
  airbnb: "Airbnb",
  phone: "Telefon",
  other: "Ostalo",
};

// ─── Guest Detail Drawer ───────────────────────────────────────────────────────

interface GuestDrawerProps {
  guest: AptGuest | null;
  open: boolean;
  onClose: () => void;
}

const GuestDrawer: React.FC<GuestDrawerProps> = ({ guest, open, onClose }) => {
  if (!guest) return null;

  const color = avatarColor(guest.key);
  const nights = guest.totalNights;
  const stays  = guest.totalStays;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={480}
      styles={{ body: { padding: 0 } }}
      title={null}
      closable={false}
    >
      {/* Header */}
      <div className="ar-guest-drawer-head" style={{ background: color }}>
        <button className="ar-guest-drawer-close" onClick={onClose}>✕</button>
        <div className="ar-guest-drawer-avatar">{initials(guest.guestName)}</div>
        <div className="ar-guest-drawer-name">{guest.guestName}</div>
        <div className="ar-guest-drawer-meta">
          {guest.guestEmail && (
            <span><MailOutlined /> {guest.guestEmail}</span>
          )}
          {guest.guestPhone && (
            <span><PhoneOutlined /> {guest.guestPhone}</span>
          )}
          {(guest as any).instagramName && (
            <span style={{ color: "#e1306c", fontWeight: 600 }}>@ {(guest as any).instagramName}</span>
          )}
        </div>
      </div>

      {/* Stats strip */}
      <div className="ar-guest-drawer-stats">
        <div className="ar-guest-stat">
          <div className="val">{stays}</div>
          <div className="lab">{stays === 1 ? "boravak" : "boravaka"}</div>
        </div>
        <div className="ar-guest-stat">
          <div className="val">{nights}</div>
          <div className="lab">{nights === 1 ? "noć" : "noći"}</div>
        </div>
        <div className="ar-guest-stat">
          <div className="val" style={{ fontSize: 12 }}>
            {fmtByCurrency(guest.spentByCurrency || {})}
          </div>
          <div className="lab">ukupno potrošeno</div>
        </div>
      </div>

      <div className="ar-guest-drawer-body">
        {/* Info rows */}
        <div className="ar-guest-drawer-section">
          <div className="ar-guest-drawer-section-title">Info</div>
          <div className="ar-guest-info-rows">
            {guest.firstStay && (
              <div className="ar-guest-info-row">
                <span className="lab"><CalendarOutlined /> Prvi boravak</span>
                <span className="val">{fmtDate(guest.firstStay)}</span>
              </div>
            )}
            {guest.lastStay && (
              <div className="ar-guest-info-row">
                <span className="lab"><CalendarOutlined /> Zadnji boravak</span>
                <span className="val">{fmtDate(guest.lastStay)}</span>
              </div>
            )}
            {guest.favoriteApartment && (
              <div className="ar-guest-info-row">
                <span className="lab"><HomeOutlined /> Omiljeni apartman</span>
                <span className="val">{guest.favoriteApartment.name}</span>
              </div>
            )}
            {guest.channels.length > 0 && (
              <div className="ar-guest-info-row">
                <span className="lab">Kanali</span>
                <span className="val">
                  {guest.channels.map((ch) => (
                    <Tag key={ch} style={{ marginRight: 4, marginBottom: 2 }}>
                      {CHANNEL_LABEL[ch] || ch}
                    </Tag>
                  ))}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Reservations history */}
        <div className="ar-guest-drawer-section">
          <div className="ar-guest-drawer-section-title">
            Historija boravaka
            <span className="ar-tab-count">{guest.recentReservations.length}</span>
          </div>
          <div className="ar-guest-res-list">
            {guest.recentReservations.map((r: AptGuestReservation) => {
              const nights = Math.max(
                0,
                dayjs(r.checkOut).diff(dayjs(r.checkIn), "day")
              );
              const ps = r.paymentStatus || (r.isPaid ? "paid" : "unpaid");
              return (
                <div key={r._id} className="ar-guest-res-item">
                  <div className="ar-guest-res-item-top">
                    <div className="ar-guest-res-dates">
                      {fmtDate(r.checkIn)} – {fmtDate(r.checkOut)}
                      <span className="nights">
                        · {nights} {nights === 1 ? "noć" : "noći"}
                      </span>
                    </div>
                    <Tag color={STATUS_COLOR[r.status]} style={{ marginLeft: "auto", flexShrink: 0 }}>
                      {STATUS_LABEL[r.status]}
                    </Tag>
                  </div>
                  <div className="ar-guest-res-item-mid">
                    {r.apartmentName && (
                      <span className="ar-guest-res-apt">
                        <HomeOutlined /> {r.apartmentName}
                      </span>
                    )}
                    <span className="ar-guest-res-channel">
                      {CHANNEL_LABEL[r.channel] || r.channel}
                    </span>
                  </div>
                  <div className="ar-guest-res-item-foot">
                    <span className={`ar-guest-res-amount ar-guest-res-amount--${ps}`}>
                      {r.amount > 0 ? fmtCurrency(r.amount, r.currency) : <span style={{ color: "#9893a8" }}>Free</span>}
                      {ps === "paid" && r.amount > 0 && <span className="badge">✓</span>}
                      {ps === "partial" && <span className="badge partial">◑ avans</span>}
                      {ps === "unpaid" && r.amount > 0 && <span className="badge unpaid">○</span>}
                    </span>
                    {r.note && (
                      <Tooltip title={r.note}>
                        <span className="ar-guest-res-note">💬 bilješka</span>
                      </Tooltip>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </Drawer>
  );
};

// ─── Main Component ────────────────────────────────────────────────────────────

interface GuestsTabProps {
  clientId: string;
}

const GuestsTab: React.FC<GuestsTabProps> = ({ clientId }) => {
  const [guests, setGuests]     = useState<AptGuest[]>([]);
  const [total, setTotal]       = useState(0);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState("");
  const [sortBy, setSortBy]     = useState<GetAptGuestsParams["sortBy"]>("lastStay");
  const [page, setPage]         = useState(1);
  const PAGE_SIZE               = 30;

  const [drawerGuest, setDrawerGuest] = useState<AptGuest | null>(null);

  const load = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      const res = await getAptGuests(clientId, {
        search: search || undefined,
        sortBy,
        order: "desc",
        page,
        limit: PAGE_SIZE,
      });
      setGuests(res.data.guests);
      setTotal(res.data.total);
    } catch {
      message.error("Greška pri učitavanju gostiju");
    } finally {
      setLoading(false);
    }
  }, [clientId, search, sortBy, page]);

  useEffect(() => { load(); }, [load]);

  // Reset page on filter change
  useEffect(() => { setPage(1); }, [search, sortBy]);

  const columns: ColumnsType<AptGuest> = [
    {
      title: "Gost",
      key: "guest",
      render: (_, g) => {
        const color = avatarColor(g.key);
        return (
          <div className="ar-guest-cell">
            <span className="ar-guest-ava-lg" style={{ background: color }}>
              {initials(g.guestName)}
            </span>
            <div>
              <div className="ar-guest-name">{g.guestName}</div>
              <div className="ar-guest-meta">
                {g.guestEmail && <span><MailOutlined style={{ fontSize: 11 }} /> {g.guestEmail}</span>}
                {g.guestPhone && <span><PhoneOutlined style={{ fontSize: 11 }} /> {g.guestPhone}</span>}
                {(g as any).instagramName && <span style={{ color: "#e1306c", fontWeight: 600, fontSize: 11 }}>@{(g as any).instagramName}</span>}
                {!g.guestEmail && !g.guestPhone && !(g as any).instagramName && <span style={{ color: "#9893a8" }}>—</span>}
              </div>
            </div>
          </div>
        );
      },
    },
    {
      title: "Boravci",
      key: "totalStays",
      align: "center" as const,
      render: (_, g) => (
        <div style={{ textAlign: "center" }}>
          <div style={{ fontWeight: 700, fontSize: 16, color: "#3d3650" }}>{g.totalStays}</div>
          <div style={{ fontSize: 11, color: "#9893a8" }}>
            {g.totalNights} {g.totalNights === 1 ? "noć" : "noći"}
          </div>
        </div>
      ),
    },
    {
      title: "Omiljeni apartman",
      key: "favoriteApartment",
      render: (_, g) => (
        <span style={{ fontSize: 13, color: g.favoriteApartment ? "#3d3650" : "#9893a8" }}>
          {g.favoriteApartment ? (
            <><HomeOutlined style={{ marginRight: 5, color: "#5b50d6" }} />{g.favoriteApartment.name}</>
          ) : "—"}
        </span>
      ),
    },
    {
      title: "Zadnji boravak",
      key: "lastStay",
      render: (_, g) => (
        <span style={{ fontSize: 13, color: "#6b6880" }}>
          {g.lastStay ? fmtDate(g.lastStay) : "—"}
        </span>
      ),
    },
    {
      title: "Ukupno potrošeno",
      key: "totalSpent",
      align: "right" as const,
      render: (_, g) => {
        const spent = g.spentByCurrency || {};
        const paid  = g.paidByCurrency  || {};
        const hasPending = Object.entries(spent).some(([cur, v]) => v > (paid[cur] || 0));
        return (
          <div style={{ textAlign: "right" }}>
            <div style={{ fontWeight: 700, fontSize: 13, color: "#3d3650" }}>
              {fmtByCurrency(spent)}
            </div>
            {hasPending && (
              <div style={{ fontSize: 11, color: "#ca8a04" }}>
                {fmtByCurrency(paid)} naplaćeno
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: "Kanali",
      key: "channels",
      render: (_, g) => (
        <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
          {g.channels.map((ch) => (
            <Tag key={ch} style={{ fontSize: 11, margin: 0 }}>
              {CHANNEL_LABEL[ch] || ch}
            </Tag>
          ))}
        </div>
      ),
    },
  ];

  return (
    <div className="ar-guests-tab">
      {/* Toolbar */}
      <div className="ar-guests-toolbar">
        <div className="ar-search" style={{ flex: 1, maxWidth: 320 }}>
          <UserOutlined />
          <input
            type="text"
            placeholder="Pretraži po imenu, emailu, telefonu..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 12, color: "#6b6880", whiteSpace: "nowrap" }}>Sortiraj po:</span>
          <Select
            value={sortBy}
            onChange={setSortBy}
            style={{ width: 160 }}
            size="small"
            options={[
              { value: "lastStay",    label: "Zadnji boravak" },
              { value: "totalSpent",  label: "Ukupno potrošeno" },
              { value: "totalStays",  label: "Broj boravaka" },
              { value: "totalNights", label: "Broj noći" },
              { value: "guestName",   label: "Ime (A–Z)" },
            ]}
          />
        </div>

        <span style={{ fontSize: 13, color: "#9893a8", marginLeft: "auto" }}>
          {total} {total === 1 ? "gost" : "gosta/gostiju"}
        </span>
      </div>

      {loading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: 60 }}>
          <Spin size="large" />
        </div>
      ) : guests.length === 0 ? (
        <div className="ar-empty">
          <UserOutlined style={{ fontSize: 42, opacity: 0.4 }} />
          <div className="t">{search ? "Nema rezultata" : "Nema gostiju"}</div>
          <div className="s">
            {search
              ? "Probajte drugi pojam za pretragu"
              : "Gosti se automatski pojavljuju iz rezervacija"}
          </div>
        </div>
      ) : (
        <div className="ar-table-wrap">
          <Table
            columns={columns}
            dataSource={guests}
            rowKey="key"
            size="middle"
            scroll={{ x: 700 }}
            onRow={(g) => ({
              onClick: () => setDrawerGuest(g),
              style: { cursor: "pointer" },
            })}
            pagination={{
              current: page,
              pageSize: PAGE_SIZE,
              total,
              onChange: setPage,
              showTotal: (t) => `${t} gostiju`,
              showSizeChanger: false,
            }}
          />
        </div>
      )}

      <GuestDrawer
        guest={drawerGuest}
        open={!!drawerGuest}
        onClose={() => setDrawerGuest(null)}
      />
    </div>
  );
};

export default GuestsTab;
